import axios from 'axios';
import endpoints from 'constants/endpoints';
import { apptStatus, bobStatus, stateAbbreviations } from 'constants/enum';
import { safeAWSCallPost, safeAWSCallPostStream } from 'helpers/firebaseSetup';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getHStabData } from 'state/actions/actions';
import { showAlert } from 'state/actions/site';

const paymentStatuses = {
  action_needed: 'Action Needed'
};

export const INITIAL_VALUES = {
  searchTerm: '',
  carriers: [],
  agents: [],
  mktp_state: [],
  typeFilter: [],
  bobFilter: [],
  carrierApptStatus: [],
  agentStatus: ['Active']
};

const useCustomersData = ({
  loadingApptsData,
  setLocalLoader,
  setFilterCount,
  setShowFilters,
  setActivePanel
}) => {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(50);
  const [formData, setformData] = useState(INITIAL_VALUES);
  const [triggerFetch, setTriggerFetch] = useState(1);
  const [loadingPotentialMatches, setLoadingPotentialMatches] = useState(false);
  const [selectedFilterData, setSelectedFilterData] = useState(INITIAL_VALUES);

  const [potentialMatchesData, setPotentialMatchesData] = useState({
    HsPolicyId: '',
    BobStatus: '',
    currentBobMatchId: '',
    npn: '',
    potentialBobMatches: []
  });

  const [potentialMatchesCount, setPotentialMatchesCount] = useState([]);

  // Loads data initially
  const cancelTokenSource = axios.CancelToken.source();

  const getHSData = React.useCallback(
    (page, filters, loadingApptsData, skipCountPages) => {
      setLocalLoader(true);
      setActivePanel({});
      dispatch(
        getHStabData(
          loadingApptsData ? () => {} : setLocalLoader,
          () => {},
          currentPageSize,
          page,
          {
            ...filters,
            mktp_state: filters.mktp_state.map(
              state => stateAbbreviations[state]
            ),
            agents: filters.agents.map(agent => agent.npn),
            carrierApptStatus: filters.carrierApptStatus.map(
              value => apptStatus[value]
            ),
            bobFilter: filters.bobFilter.map(value => bobStatus[value]),
            agentStatus: filters.agentStatus,
            skipCountPages
          },
          filters.agents.map(agent => agent.id.toString())
        )
      );
      const tbodyElement = document.getElementById('customers-table-body');
      tbodyElement.scrollTop = 0;
    },
    [currentPageSize]
  );

  const getPotentialMatches = useCallback(
    async (hsPolicyId, npn, BobStatus) => {
      setLoadingPotentialMatches(true);
      safeAWSCallPost(
        endpoints.aws.HSGetPotentialBobMatches,
        {
          hsPolicyId,
          npn,
          BobStatus
        },
        false,
        cancelTokenSource
      )
        .then(async HStabdata => {
          setPotentialMatchesData(potentialMatches => {
            return {
              ...potentialMatches,
              currentBobMatchId: HStabdata?.data?.currentBobMatchId,
              potentialBobMatches: HStabdata?.data?.potentialBobMatches
            };
          });
          setLoadingPotentialMatches(false);
        })
        .catch(error => {
          console.error(error);
          setLoadingPotentialMatches(false);
          dispatch(
            showAlert({
              message: 'Could not load potential BOB matches in progress',
              code: 'error'
            })
          );
        });
    },
    [potentialMatchesData.HsPolicyId]
  );

  const deleteBobMatch = useCallback(async () => {
    safeAWSCallPost(
      endpoints.aws.HSRemoveCurrentAssociation,
      {
        hsPolicyId: potentialMatchesData.HsPolicyId,
        BOB_Id: potentialMatchesData.currentBobMatchId
      },
      false,
      cancelTokenSource
    )
      .then(async () => {
        setPotentialMatchesData(potentialMatches => {
          return {
            ...potentialMatches,
            BobStatus: 'ManuallyUnmatched',
            currentBobMatchId: ''
          };
        });
        getPotentialMatches(
          potentialMatchesData.HsPolicyId,
          potentialMatchesData.npn,
          'ManuallyUnmatched'
        );
        setTriggerFetch(num => num + 1);
      })
      .catch(error => {
        console.error(error);
        dispatch(
          showAlert({
            message: 'Could not remove BOB match',
            code: 'error'
          })
        );
      });
  }, [setPotentialMatchesData, potentialMatchesData, getPotentialMatches]);

  const confirmBobMatch = useCallback(
    async (bobNpn, bobId) => {
      safeAWSCallPost(
        endpoints.aws.HSConfirmCurrentAssociation,
        {
          hsPolicyId: potentialMatchesData.HsPolicyId,
          npn: potentialMatchesData.npn,
          BOB_Id: bobId,
          BOB_Npn: bobNpn
        },
        false,
        cancelTokenSource
      )
        .then(async () => {
          setPotentialMatchesData(potentialMatches => {
            return {
              ...potentialMatches,
              BobStatus: 'ManuallyMatched',
              currentBobMatchId: bobId
            };
          });
          getPotentialMatches(
            potentialMatchesData.HsPolicyId,
            potentialMatchesData.npn,
            'ManuallyUnmatched'
          );
          setTriggerFetch(num => num + 1);
        })
        .catch(error => {
          console.error(error);
          dispatch(
            showAlert({
              message: 'Could not remove BOB match',
              code: 'error'
            })
          );
        });
    },
    [setPotentialMatchesData, potentialMatchesData, getPotentialMatches]
  );

  React.useEffect(() => {
    if (formData.agents.length === 0) return;
    // HS data
    getHSData(currentPage, formData, loadingApptsData, true);

    return () => {
      cancelTokenSource.cancel('API Call Canceled');
    };
  }, [triggerFetch]);

  // Raw policies data
  const { policies, agent_policies_count } = useSelector(
    state => state.healthSherpaData
  );

  // Transform data to fit into the table
  const data_for_table = React.useMemo(() => {
    const policiesData = policies.map(x => {
      const paymentSt = x?.followups?.length;

      const { residence, assistor } = x;

      return {
        ...x,
        HealthSherpaId: x.Policies_healthSherpaId,
        Client: `${x.demographics_firstName || ''} ${
          x.demographics_middleName || ''
        } ${x.demographics_lastName || ''}`,
        Carrier: x.issuer_name,
        CarrierPolicyNumber: x.mktp_issuer_assigned_policy_id || '-',
        IssuerSubID: x.issuer_assigned_subscriber_id || '-',
        FFMSubID: x.mktp_issuer_ffm_subscriber_id || '-',
        FFMAppID: x.ffm_app_id || '-',
        AorName: `${x?.first_name} ${x?.last_name}` || '-',
        State: x.mktp_state,
        CarrierId: x.carrier_id,
        Created: x.mktp_created_at,
        Effective: x.mktp_effective_date,
        Plan: x.plan_name,
        Gross: x.grossPremium,
        Net: x.netPremium,
        Payment: paymentSt ? paymentStatuses[x.followups[0].status] : 'Unknown',
        CarrierApptStatus: x?.carrier_audit_status || 'NotAppointed',
        BobStatus: x?.bob_status || 'PendingMatch',
        Residence: `${residence?.streetAddress}, ${residence?.zipCode} ${residence?.county}, ${residence?.city}`,
        AssistorNpn: x?.assistor_nationalProducerNumber,
        AssistorName: `${assistor?.agentBrokerFirstName || ''} ${
          assistor?.agentBrokerLastName || ''
        }`,
        AssistorAgency: assistor?.agency_name || ''
      };
    });

    return policiesData;
  }, [policies]);

  React.useEffect(() => {
    setPotentialMatchesCount([]);
    if (data_for_table.length === 0) {
      return;
    }

    // Fetch potential matches for each policy
    const fetchFor = data_for_table.filter(
      policy =>
        [
          'PendingMatch',
          'TwoWeeksPast',
          'Ignored',
          'ManuallyUnmatched',
          'AssignedToOther'
        ].includes(policy.BobStatus) && policy.HealthSherpaId
    );

    const fetchData = fetchFor.map(policy => ({
      hsPolicyId: policy.HealthSherpaId,
      npn: policy.AssistorNpn,
      BobStatus: policy.BobStatus
    }));

    safeAWSCallPostStream(endpoints.aws.HSGetPotentialBobMatchesBulk, fetchData)
      .then(async res => {
        const isReadable =
          res && res.body && typeof res.body.getReader === 'function';

        // Helper: push parsed JSON value(s) to state
        const pushParsed = jsonString => {
          try {
            const parsed = JSON.parse(jsonString);
            const items = Array.isArray(parsed) ? parsed : [parsed];
            setPotentialMatchesCount(currentData => [...currentData, ...items]);
            return true;
          } catch (e) {
            return false;
          }
        };

        // Helper: process buffer to extract complete top-level JSON values
        const processBufferValues = bufferInput => {
          let buffer = bufferInput;
          // Fast-path: also handle NDJSON by lines if present
          if (buffer.includes('\n')) {
            const parts = buffer.split('\n');
            // Keep last fragment in buffer; try parse the rest
            const last = parts.pop();
            for (const part of parts) {
              const trimmed = part.trim();
              if (!trimmed) continue;
              pushParsed(trimmed);
            }
            buffer = last;
          }

          // General path: bracket/quote-aware scan for complete JSON from start
          let startIndex = -1;
          let depth = 0;
          let inString = false;
          let escapeNext = false;
          for (let i = 0; i < buffer.length; i += 1) {
            const ch = buffer[i];
            if (startIndex === -1) {
              if (/\s/.test(ch)) continue;
              if (ch === '{' || ch === '[') {
                startIndex = i;
                depth = 1;
                continue;
              } else {
                // Unknown prefix, drop until next structural token
                continue;
              }
            }

            if (inString) {
              if (escapeNext) {
                escapeNext = false;
              } else if (ch === '\\') {
                escapeNext = true;
              } else if (ch === '"') {
                inString = false;
              }
              continue;
            }

            if (ch === '"') {
              inString = true;
              continue;
            }
            if (ch === '{' || ch === '[') depth += 1;
            else if (ch === '}' || ch === ']') depth -= 1;

            if (depth === 0 && startIndex !== -1) {
              const candidate = buffer.slice(startIndex, i + 1);
              const ok = pushParsed(candidate);
              if (!ok) break; // need more data
              // Remove consumed segment and restart scanning
              buffer = buffer.slice(i + 1);
              i = -1;
              startIndex = -1;
            }
          }
          return buffer;
        };

        if (!isReadable) {
          // Fallback for environments without ReadableStream support
          try {
            if (typeof res.text === 'function') {
              const text = await res.text();
              let remaining = text || '';
              remaining = processBufferValues(remaining);
              const tail = remaining.trim();
              if (tail) pushParsed(tail);
              return;
            }
            if (typeof res.json === 'function') {
              const data = await res.json();
              const items = Array.isArray(data) ? data : [data];
              setPotentialMatchesCount(currentData => [
                ...currentData,
                ...items
              ]);
              return;
            }
            throw new Error('Response body is not a readable stream');
          } catch (fallbackErr) {
            console.error('Fallback parse error:', fallbackErr);
            throw fallbackErr;
          }
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            // Flush decoder and process any remaining buffered data
            const flushed = decoder.decode();
            if (flushed) buffer += flushed;
            buffer = processBufferValues(buffer);
            const tail = buffer.trim();
            if (tail) pushParsed(tail);
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          buffer = processBufferValues(buffer);
        }
      })
      .then(() => {})
      .catch(err => console.error('Error sending stream:', err));
  }, [data_for_table]);

  const handleChangeSelectValue = useCallback(
    (_values, property) => {
      let values = [];
      if (typeof _values === 'string') {
        values = formData[property][0] === _values ? [] : [_values];
      } else {
        values = _values;
      }
      if (
        formData[property].length === 0 &&
        values.length > 0 &&
        property !== 'agents'
      ) {
        setFilterCount(count => (count += 1));
      } else if (
        formData[property].length > 0 &&
        values.length === 0 &&
        property !== 'agents'
      ) {
        setFilterCount(count => (count -= 1));
      }
      setformData(prevState => ({
        ...prevState,
        [property]: values
      }));
    },
    [formData]
  );

  const clearFilters = useCallback(() => {
    const clearedFormData = {
      ...INITIAL_VALUES,
      agents: [...formData.agents]
    };
    // Calculate filter count: agentStatus always has 'Active' by default, so count is 1
    setFilterCount(1);
    setformData(clearedFormData);
    setSelectedFilterData(clearedFormData);
  }, [setFilterCount, setformData, setSelectedFilterData, formData.agents]);

  const applyFilters = useCallback(
    (reset = false) => {
      if (formData.agents.length === 0) return;
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      if (reset) {
        const resetFormData = {
          ...INITIAL_VALUES,
          agents: [...formData.agents]
        };
        getHSData(currentPage, resetFormData, false, false);
      } else {
        getHSData(currentPage, formData, false, false);
      }
      setShowFilters(false);
    },
    [currentPage, getHSData, formData]
  );

  return {
    data_for_table,
    currentPage,
    setCurrentPage,
    setCurrentPageSize,
    agent_policies_count,
    formData,
    setformData,
    clearFilters,
    handleChangeSelectValue,
    applyFilters,
    setTriggerFetch,
    setPotentialMatchesData,
    potentialMatchesData,
    loadingPotentialMatches,
    deleteBobMatch,
    getPotentialMatches,
    confirmBobMatch,
    selectedFilterData,
    setSelectedFilterData,
    potentialMatchesCount
  };
};

useCustomersData.propTypes = {
  loadingApptsData: PropTypes.func,
  setLocalLoader: PropTypes.func,
  setFilterCount: PropTypes.func,
  setShowFilters: PropTypes.func,
  setActivePanel: PropTypes.func
};

export default useCustomersData;
